import axios from "axios";
import {ProblemDetail} from "@/api/types";

/**
 * Helpers to read the RFC 9457 `ProblemDetail` out of a rejected API call.
 *
 * The response interceptor in `http.ts` rejects with the original `AxiosError`, so nothing about the error is lost
 * (`error.response`, `error.config`, ...). Pages that need to react to a specific failure use these helpers instead
 * of digging through `error.response.data` themselves:
 *
 *     try {
 *         await createPartner(input, {skipErrorToast: true});
 *     } catch (e) {
 *         if (isProblem(e, {status: 409, code: "PARTNER_NAME_TAKEN"})) {
 *             this.nameError = problemOf(e)!.detail;
 *         }
 *     }
 */

export interface ProblemMatch {
    status?: number;
    code?: string;
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
    typeof value === "object" && value !== null && !Array.isArray(value);

const str = (value: unknown): string | undefined =>
    typeof value === "string" && value !== "" ? value : undefined;

/**
 * The problem detail of a failed API call, or `null` when `e` is not an HTTP error response (network failure,
 * cancelled request, programming error).
 *
 * `status` always comes from the HTTP status line. The other fields come from the JSON body when it is a problem
 * detail; for responses without one (the 401 of the security entry point, a proxy error page) they are `undefined`.
 */
export function problemOf(e: unknown): ProblemDetail | null {
    if (!axios.isAxiosError(e) || !e.response) {
        return null;
    }

    const body = isRecord(e.response.data) ? e.response.data : {};

    return {
        status: e.response.status,
        detail: str(body.detail),
        code: str(body.code),
        type: str(body.type),
        title: str(body.title),
        instance: str(body.instance),
    };
}

/** True when `e` is an HTTP error response that matches `match` (any `status` and `code` when omitted). */
export function isProblem(e: unknown, match: ProblemMatch = {}): boolean {
    const problem = problemOf(e);

    return problem !== null
        && (match.status === undefined || problem.status === match.status)
        && (match.code === undefined || problem.code === match.code);
}

/** Text for the snackbar: the problem `detail`, or `"Server error"` when there is none. */
export function errorMessage(e: unknown): string {
    return problemOf(e)?.detail ?? "Server error";
}
